import React, { useState, useEffect, Suspense, lazy } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import courseService from '../../../../services/courseService';
import type { ICourse } from '../../../../types/course';
import Loader from '../../../../components/common/Loader';
import { showToast } from '../../../../utils/showToast';
import { ArrowLeft, BookOpen, X as CloseIcon, FileText } from 'lucide-react';

const CourseMaterialViewer = lazy(() => import('../components/CourseMaterialViewer'));

// Reusable ModulesList component
const ModulesList: React.FC<{ course: ICourse, onViewMaterial: (url: string) => void }> = ({ course, onViewMaterial }) => {
    return (
        <div className="space-y-4">
            {course.modules.length === 0 ? (
                <p className="text-gray-500 text-sm">No modules are available for this course yet.</p>
            ) : (
                course.modules.map((module) => (
                    <div key={module._id} className="border-l-4 border-blue-500 pl-4 py-2 bg-gray-50/50 rounded-r-lg">
                        <h4 className="font-semibold text-lg text-gray-800">{module.title}</h4>
                        <p className="text-sm text-gray-600 mb-3">{module.description}</p>
                        {module.materials && module.materials.length > 0 && (
                            <div className="space-y-2">
                                {module.materials.map((material, materialIndex) => (
                                    <button
                                        key={materialIndex}
                                        onClick={() => onViewMaterial(material.url)}
                                        className="flex items-center text-sm text-blue-600 hover:underline hover:text-blue-800 text-left"
                                    >
                                        <FileText className="w-4 h-4 mr-2 flex-shrink-0" />
                                        <span>{material.name} ({material.type.toUpperCase()})</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                ))
            )}
        </div>
    );
};

const CourseModulesPage: React.FC = () => {
    const { courseId } = useParams<{ courseId: string }>();
    const [course, setCourse] = useState<ICourse | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [viewingMaterialUrl, setViewingMaterialUrl] = useState<string | null>(null);

    useEffect(() => {
        const fetchCourse = async () => {
            if (!courseId) return;
            setIsLoading(true);
            try {
                // ✅ FIX: Call the correct service function for enrolled students
                const courseData = await courseService.getEnrolledCourseById(courseId); 
                setCourse(courseData);
            } catch (err: any) {
                showToast(err.message, { type: 'error' });
                setCourse(null); // Ensure course is null on error
            } finally {
                setIsLoading(false);
            }
        };
        fetchCourse();
    }, [courseId]);
    
    const handleViewMaterial = (materialUrl: string) => {
        setViewingMaterialUrl(materialUrl);
    };

    if (isLoading) return <Loader />;
    if (!course) return <p className="text-center text-red-500">Course not found.</p>;

    return (
        <>
            <Helmet>
                <title>{course.title} | My Learning</title>
            </Helmet>
            <div className="space-y-6">
                <Link to="/dashboard/student/my-courses" className="inline-flex items-center gap-2 text-blue-600 hover:underline font-medium">
                    <ArrowLeft size={16} /> Back to My Courses
                </Link>

                <header className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
                    <h1 className="text-3xl font-bold text-gray-800">{course.title}</h1>
                    <p className="text-gray-500 mt-2">{course.description}</p>
                </header>

                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
                    <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-3 mb-4">
                        <BookOpen /> Course Modules
                    </h2>
                    <ModulesList course={course} onViewMaterial={handleViewMaterial} />
                </div>
            </div>

            <Suspense fallback={<Loader />}>
                {viewingMaterialUrl && (
                    <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
                        <button onClick={() => setViewingMaterialUrl(null)} className="absolute top-4 right-4 text-white hover:text-gray-300 z-50" aria-label="Close material viewer">
                            <CloseIcon size={32} />
                        </button>
                        <CourseMaterialViewer materialUrl={viewingMaterialUrl} />
                    </div>
                )}
            </Suspense>
        </>
    );
};

export default CourseModulesPage;